<script>
  import { onMount } from "svelte";
  import SpotlightGuide from "@components/common/SpotlightGuide.svelte";
  import { registerViewTour } from "lib/stores/viewTourStore.js";
  import { ROUTES } from "@utils/routes.js";
  import { useUrlParams } from "@stores/urlStore.js";
  import Header from "@components/Header.svelte";
  import { ToastNotification } from "carbon-components-svelte";
  import { fly } from "svelte/transition";
  import ProjectStepActions from "../components/ProjectStepActions.svelte";
  import { PROJECT_STAGES } from "@utils/stagesAndSteps.js";
  import WorkshopColumn from "@components/WorkshopColumn.svelte";
  import { InfoBlock } from "paxray-components-svelte";

  const urlParams = useUrlParams();
  const step = PROJECT_STAGES.SETUP.steps.WORKSHOP;
  const stage = PROJECT_STAGES.SETUP.id;

  let toastMsg;
  let saveSuccessful;
  let copiedResourceId = null;
  let showWorkshopGuide = false;
  let workshopGuideStep = 0;
  let workshopGuideSteps = [];

  onMount(() =>
    registerViewTour(() => {
      workshopGuideSteps = [
        {
          target: '[data-guide="workshop-resources"]',
          title: "Align your team in the setup workshop",
          text: "After defining your project scope and setting up your teams, use the workshop to align participants on project goals and how to take part.",
        },
        {
          target: '[data-guide="workshop-downloads"] .workshop_column_items',
          title: "Prepare your workshop materials",
          text: "Use the workshop slides to explain the project and the task registration guide to introduce the client. Both downloads are currently unavailable. Contact your admin for the materials.",
        },
        {
          target:
            '[data-guide="workshop-project-links"] .workshop_column_items',
          title: "Share the participant project link",
          text: "Participants can use this link to add their own restrictions on data collection. Open it to review the participant view, or use Copy Link to share it with your team after the tour.",
        },
        {
          target:
            '[data-guide="workshop-external-resources"] .workshop_column_items',
          title: "Find help with task registration",
          text: "The Help Center provides a short video and FAQs for the later task registration phase. Open it yourself or copy the link to share it with participants.",
        },
        {
          target:
            '[data-guide="complete-workshop"] > button, [data-guide="complete-workshop"] > .completed-badge',
          title: "Complete and lock the workshop",
          text: 'Only use "Complete & Lock Workshop" after the workshop has taken place and participants have received the relevant information. Completing this step locks it and cannot be undone. Once complete, the button is replaced by "Completed". The next setup task is "Exclude Apps & Websites".',
        },
      ].filter(({ target }) => {
        const element = document.querySelector(target);
        return (
          element &&
          getComputedStyle(element).visibility === "visible" &&
          element.getBoundingClientRect().width > 0 &&
          element.getBoundingClientRect().height > 0
        );
      });
      workshopGuideStep = 0;
      showWorkshopGuide = workshopGuideSteps.length > 0;
    }),
  );

  let downloads = [
    {
      id: 1,
      name: "Workshop slides",
      description:
        "Workshop slides to explain the solution and the project steps to all participants",
      type: "PDF",
      tag: "Presentation",
      icon: "download",
      buttonText: "Not available. Contact your admin",
      file: "/downloads/workshop/workshop-slides.pdf",
      unavailable: true,
    },
    {
      id: 2,
      name: "Task registration guide",
      description:
        "A brief guide explaining how to use the client during task registration",
      type: "PDF",
      tag: "Guide",
      icon: "task",
      buttonText: "Not available. Contact your admin",
      file: "/downloads/workshop/task-registration-guide.pdf",
      unavailable: true,
    },
  ];

  $: projectLink = `/UserInput?idproject=${idproject}&currentStep=0`;
  $: projectsLinks = [
    {
      id: 1,
      name: "Participant Project Link",
      description:
        "Link for participants to add their own restrictions on data collection.",
      type: "Platform Link",
      tag: "Participant",
      icon: "open_in_new",
      actionIcon: "open_in_new",
      url: projectLink,
      buttonText: "Open Participant Link",
    },
  ];

  let externalResources = [
    {
      id: 1,
      name: "Task Registration Help Center",
      description:
        "Further information on task registration, including a short video and FAQs",
      type: "External Link",
      tag: "Help Center",
      icon: "open_in_new",
      actionIcon: "open_in_new",
      url: "https://paxray.notion.site/task-registration-guide",
      buttonText: "Open Help Center",
    },
  ];

  $: ({ idproject } = $urlParams);
  $: projectProgressHref = `${ROUTES.dam.progress}?${new URLSearchParams({ idproject }).toString()}`;
  $: resourceSections = [
    {
      id: "downloads",
      title: "Downloads",
      accentColor: "#b7e3bd",
      tooltipText: "Workshop presentations and guides.",
      resources: downloads,
    },
    {
      id: "project-links",
      title: "Project Links",
      accentColor: "#abcfe8",
      tooltipText: "Links for project participants.",
      resources: projectsLinks,
      handleAction: (resource) => window.open(resource.url, "_blank"),
    },
    {
      id: "external-resources",
      title: "External Resources",
      accentColor: "#e5e8a8",
      tooltipText: "Additional workshop resources.",
      resources: externalResources,
      handleAction: (resource) => window.open(resource.url, "_blank"),
    },
  ];

  function copyLinkFallback(link) {
    const textarea = document.createElement("textarea");
    textarea.value = link;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.left = "-9999px";
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }

  async function copyResourceLink(resource, sectionId) {
    if (!resource.url) return;
    const link = new URL(resource.url, window.location.origin).href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        copyLinkFallback(link);
      }
    } catch (error) {
      try {
        copyLinkFallback(link);
      } catch (fallbackError) {
        console.error("Copy link failed", fallbackError);
        return;
      }
    }

    copiedResourceId = `${sectionId}-${resource.id}`;
    setTimeout(() => {
      copiedResourceId = null;
    }, 2000);
  }

  $$restProps;
</script>

<Header
  section="Project"
  area="Setup"
  breadcrumb="Inform Participants"
  areaHref={`${projectProgressHref}#setup-workshop`}
  tooltipText="Prepare and run the workshop with your team. After completion, this step cannot be changed."
>
  <div data-guide="complete-workshop">
    <ProjectStepActions
      {idproject}
      {step}
      {stage}
      showGoBackButton={false}
      bind:toastMsg
      bind:saveSuccessful
      text="Complete & Lock Workshop"
      size="sm"
      completionWarning="After completion, the workshop step cannot be changed. Please make sure the workshop has been prepared and completed before continuing. This action cannot be undone."
    />
  </div>
</Header>

<div class="main">
  <div data-guide="workshop-resources">
    <InfoBlock
      type="info"
      heading="Workshop resources"
      message="Use the resources below to prepare and run the workshop. Once participants have received the relevant information, click &quot;Complete &amp; Lock Workshop&quot;."
      seamless={true}
      showClose={true}
    />
  </div>
  <div class="resource_grid">
    {#each resourceSections as section}
      <section class="resource_section" data-guide={`workshop-${section.id}`}>
        <WorkshopColumn
          headerName={section.title}
          resources={section.resources}
          accentColor={section.accentColor}
          tooltipText={section.tooltipText}
          id={section.id}
          handleAction={section.handleAction}
          {copyResourceLink}
          bind:copiedResourceId
        />
      </section>
    {/each}
  </div>
</div>

<SpotlightGuide
  bind:open={showWorkshopGuide}
  bind:currentStep={workshopGuideStep}
  steps={workshopGuideSteps}
  allowInteraction={false}
/>

{#if toastMsg}
  <div
    in:fly={{ x: 100 }}
    out:fly={{ y: 100 }}
    style="position:fixed; top:60px; right:20px; z-index:100;"
  >
    <ToastNotification
      fullWidth
      hideAfter={saveSuccessful ? 3000 : null}
      timeout={saveSuccessful ? 3000 : null}
      kind={saveSuccessful ? "success" : "error"}
      title={saveSuccessful ? "Success" : "Error"}
      subtitle={toastMsg}
      caption={new Date().toLocaleString()}
      on:close={(e) => {
        e.preventDefault();
        toastMsg = null;
        saveSuccessful = false;
      }}
    />
  </div>
{/if}

<style>
  .main {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 100vw;
    width: 100%;
    min-height: calc(100vh - 4rem);
    background: var(--pax-white, #f9fbff);
  }

  .resource_grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: stretch;
    gap: 0;
    flex: 1;
    min-height: 0;
  }

  .resource_section {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
    border-left: 1px solid var(--border-color, #d9d9d9);
  }

  .resource_section:first-child {
    border-left: 0;
  }

  .resource_section :global(.seamless_grid_header_value) {
    background: var(--pax-black, #1c1c20);
    color: var(--pax-white, #fff);
  }

  @media (max-width: 64rem) {
    .resource_grid {
      grid-template-columns: repeat(3, minmax(18rem, 1fr));
    }
  }

  @media (max-width: 48rem) {
    .resource_grid {
      grid-template-columns: 1fr;
    }

    .resource_section {
      border-left: 0;
      border-bottom: 1px solid var(--border-color, #d9d9d9);
    }
  }
</style>
